import React from "react";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import { SizedDropdownDataInput } from "../../../components/dropdownDataInput/DropdownDataInput";
import { SizedTextDataInput } from "../../../components/textDataInput/TextDataInput";
import {
	SELECTED_DATE_RANGE_ID_STATE_KEY,
	POOL_WEIGHT_SETTING_CODE_STATE_KEY,
	POOL_WEIGHT_SETTING_CODES_KEY,
	TEXT_INPUT,
} from "../../../Consts";
import { IUsagePoolDatesAndDimensions } from "../../../types/usageTypes/IUsagePoolDatesAndDimensions";
import { updateUsagePoolField } from "../../../../redux/reducers/RepertoireReducer";
import { IRepertoireStateKeys } from "../../../types/IRepertoireStateKeys";
import { IUsagePoolStateKeys } from "../../../../redux/types/IUsagePoolState";
import ShowMore from "../../../components/showMore/ShowMore";
import { IShowMoreItem } from "../../../types/IShowMoreItem";
import { IUsagePoolWeightSettingCodesDataItems } from "../../../types/usageTypes/IUsageWeightSettingCodesDataItems";
import { IUsagePoolWeightCodeMultiplierDataItems } from "../../../types/usageTypes/IUsagePoolWeightCodeMultiplierDataItems";

export interface IPoolMaintenanceWeightSettingCodesGridProps {
	weightSettingCodesMaintenanceFields: IRepertoireComponentDataItem;
	datesAndDimensions?: IUsagePoolDatesAndDimensions[];
	weightSettingCodes?: IUsagePoolWeightSettingCodesDataItems[];
	isReadonly: boolean;
	selectedDimensionAndDateRange?: IUsagePoolDatesAndDimensions;
	setSelectedDateRange: (value: IUsagePoolDatesAndDimensions) => void;
	updateUsagePoolField: typeof updateUsagePoolField;
	exportWeightSettingCodes: (poolDimensionsAndDatesID: number) => void;
	importWeightSettingCodes: (poolDimensionsAndDatesID: number) => void;
}
interface IPoolMaintenanceWeightSettingCodesGridState {
	loaded: boolean;
	weightCodeValue: string;
	currentWeightCodeMultiplier?: IUsagePoolWeightCodeMultiplierDataItems[];
}

export default class PoolMaintenanceWeightSettingCodesGrid extends React.PureComponent<
	IPoolMaintenanceWeightSettingCodesGridProps,
	IPoolMaintenanceWeightSettingCodesGridState
> {
	viewData;
	constructor(props: IPoolMaintenanceWeightSettingCodesGridProps) {
		super(props);

		this.state = { loaded: false, weightCodeValue: "" };
		this.viewData = {};
	}

	componentDidMount() {
		const {
			weightSettingCodesMaintenanceFields: { fields },
			weightSettingCodes,
			setSelectedDateRange,
			datesAndDimensions,
		} = this.props;
		if (fields) {
			const visibleFields = fields.filter((field) => !field.hidden);
			visibleFields.forEach((item) => {
				this.viewData[item.name] = item.data;

				if (
					Object.keys(this.viewData).length === visibleFields.length
				) {
					this.setState({ loaded: true });
				}
			});
		}

		if (weightSettingCodes.length > 0) {
			const firstWeightSettingCode = weightSettingCodes.find(
				(weightSettingCode) =>
					datesAndDimensions.some(
						(datesAndDimension) => 
							weightSettingCode.effectiveDateRange.value === datesAndDimension.effectiveDateRange
					)
			);

			const selectedDimensionAndDateRange = datesAndDimensions.find(
				(dateAndDimension) =>
					dateAndDimension.effectiveDateRange ===
					firstWeightSettingCode.effectiveDateRange.value
			);

			setSelectedDateRange(selectedDimensionAndDateRange);

			this.setState({
				weightCodeValue: firstWeightSettingCode.weightSettingCode.value,
			});
		}
	}

	getEffectiveDateRange = () => {
		const { datesAndDimensions } = this.props;

		const dateRanges = datesAndDimensions?.map((dateAndDimension) => {
			return {
				code: dateAndDimension.poolDimensionsAndDatesID?.toString(),
				description: dateAndDimension.effectiveDateRange,
			};
		});

		return dateRanges;
	};

	updateWeightSettingCodes() {
		const {
			weightSettingCodes,
			selectedDimensionAndDateRange,
			updateUsagePoolField,
		} = this.props;
		const { weightCodeValue } = this.state;
		let currentWeightSettingCodes: IUsagePoolWeightSettingCodesDataItems[] = [];
		const exist = weightSettingCodes.some(
			(c) =>
				c.poolDimensionsAndDatesID ===
				selectedDimensionAndDateRange.poolDimensionsAndDatesID
		);

		if (exist) {
			currentWeightSettingCodes = weightSettingCodes.map((item) =>
				item.poolDimensionsAndDatesID ===
				selectedDimensionAndDateRange.poolDimensionsAndDatesID
					? {
							...item,
							weightSettingCode: {
								value: weightCodeValue,
								inputType: TEXT_INPUT,
							},
							effectiveDateRange: {
								value: selectedDimensionAndDateRange.effectiveDateRange,
								inputType: TEXT_INPUT,
							}
					  }
					: item
			);
		} else {
			currentWeightSettingCodes = [
				...weightSettingCodes,
				{
					poolDimensionsAndDatesID:
						selectedDimensionAndDateRange.poolDimensionsAndDatesID,
					weightSettingCode: {
						value: weightCodeValue,
						inputType: TEXT_INPUT,
					},
					effectiveDateRange: {
						value: selectedDimensionAndDateRange.effectiveDateRange,
						inputType: TEXT_INPUT,
					},
				},
			];
		}
		updateUsagePoolField(
			currentWeightSettingCodes,
			POOL_WEIGHT_SETTING_CODES_KEY as IUsagePoolStateKeys
		);
	}

	changeData = (value: any, name: IRepertoireStateKeys) => {
		if (value) {
			const {
				weightSettingCodes,
				datesAndDimensions,
				setSelectedDateRange
			} = this.props;
			if (name === SELECTED_DATE_RANGE_ID_STATE_KEY) {
				const formattedValue = Number(value);
				const selectedDateRange = datesAndDimensions.find(
					(item) => item.poolDimensionsAndDatesID === formattedValue
				);
				setSelectedDateRange(selectedDateRange);

				const selectedDateRangeInfo = weightSettingCodes?.filter(
					(item) => item.effectiveDateRange.value === selectedDateRange.effectiveDateRange
				);
				this.setState(
					{
						weightCodeValue:
							selectedDateRangeInfo.length > 0
								? selectedDateRangeInfo[0].weightSettingCode.value
								: "",
					}
				);
			} else {
				this.setState({ weightCodeValue: value }, () => {
					this.updateWeightSettingCodes();
				});
			}
		}
	};

	renderActions = (weightSettingCodesDateRangeID) => {
		const { exportWeightSettingCodes, importWeightSettingCodes } =
			this.props;
		const actions: IShowMoreItem[] = [
			{
				text: "Export",
				icon: "icon ms-Icon ms-Icon--Export",
				onClick: () => {
					exportWeightSettingCodes(weightSettingCodesDateRangeID);
				},
				isIcon: true,
			},
			{
				text: "Import",
				icon: "icon ms-Icon ms-Icon--Import",
				onClick: () => {
					importWeightSettingCodes(weightSettingCodesDateRangeID);
				},
				isIcon: true,
			},
		];

		return (
			<ShowMore
				options={actions}
				disable={!weightSettingCodesDateRangeID}
			/>
		);
	};

	render() {
		const { isReadonly, selectedDimensionAndDateRange } = this.props;

		const { loaded, weightCodeValue } = this.state;

		if (!loaded) return <div>Loading...</div>;

		return (
			<>
				<div className="row tableContainer flexRow flexAlignItemsCenter">
					<SizedDropdownDataInput
						label={this.viewData[SELECTED_DATE_RANGE_ID_STATE_KEY]}
						fieldName={SELECTED_DATE_RANGE_ID_STATE_KEY}
						value={selectedDimensionAndDateRange?.poolDimensionsAndDatesID}
						changeData={this.changeData.bind(this)}
						options={this.getEffectiveDateRange()}
						allowNull={true}
						isHidden={
							!this.viewData[SELECTED_DATE_RANGE_ID_STATE_KEY]
						}
						readOnly={isReadonly}
					/>
					<SizedTextDataInput
						label={
							this.viewData[POOL_WEIGHT_SETTING_CODE_STATE_KEY]
						}
						fieldName={POOL_WEIGHT_SETTING_CODE_STATE_KEY}
						changeData={this.changeData.bind(this)}
						value={weightCodeValue}
						isHidden={
							!this.viewData[POOL_WEIGHT_SETTING_CODE_STATE_KEY]
						}
						readOnly={!selectedDimensionAndDateRange}
					/>
					<div key={"showMore"} className={"flexRow"}>
						{this.renderActions(
							selectedDimensionAndDateRange?.poolDimensionsAndDatesID
						)}
					</div>
				</div>
			</>
		);
	}
}
